import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Section from './Section';
import DiaryItemCreateModal from './DiaryItemCreateModal';
import PlanAgendaCard from './PlanAgendaCard';
import type { DiaryItem } from '../models/diary';
import type { DiaryRecord } from '../models/diaryRecord';
import { diaryService } from '../services/diaryService';
import { diaryRecordsService } from '../services/diaryRecordsService';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  DAY_NAMES,
  formatDDMMYYYY,
  getWeekDays,
  getDaysFromToday,
  isSameDay,
  recordDatePart,
  parseDDMMYYYY,
} from '../utils/weekDates';

type ItemType = 'FitnessItem' | 'SupplementItem' | 'MedicationItem' | 'MealItem';

interface PlanPanelProps {
  patientId: string;
  patientUserId: string | number;
}

const CREATE_BUTTONS: { type: ItemType; label: string; icon: string; bg: string; hover: string }[] = [
  { type: 'FitnessItem', label: 'Actividad', icon: '🏃', bg: '#38b2ac', hover: '#319795' },
  { type: 'SupplementItem', label: 'Suplemento', icon: '💊', bg: '#8b5cf6', hover: '#7c3aed' },
  { type: 'MedicationItem', label: 'Medicamento', icon: '💉', bg: '#f97316', hover: '#ea6c00' },
  { type: 'MealItem', label: 'Comida', icon: '🍽️', bg: '#10b981', hover: '#059669' },
];

const SHORT_DAY = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// Whether a (recurring) plan item is active on a given calendar date,
// respecting its effective/cancellation window when present.
const isItemActiveOn = (item: DiaryItem, date: Date): boolean => {
  const eff = parseDDMMYYYY(item.effectiveDate as string | null | undefined);
  if (eff && date < eff) return false;
  const cancel = parseDDMMYYYY(item.cancellationDate as string | null | undefined);
  if (cancel && date > cancel) return false;
  return true;
};

const PlanPanel: React.FC<PlanPanelProps> = ({ patientId, patientUserId }) => {
  const [planItems, setPlanItems] = useState<DiaryItem[]>([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  const [records, setRecords] = useState<DiaryRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const [viewMode, setViewMode] = useState<'expanded' | 'condensed'>('expanded');
  const [weekOffset, setWeekOffset] = useState(0); // expanded: weeks from current
  const [condensedOffset, setCondensedOffset] = useState(0); // condensed: days from today

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DiaryItem | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalItemType, setCreateModalItemType] = useState<ItemType>('FitnessItem');
  const [createDefaultDay, setCreateDefaultDay] = useState<string>('Monday');

  const isMobile = useIsMobile();
  const CONDENSED_DAYS = 3;
  // On mobile we force a single-day view (one full-width column, day-by-day nav).
  const effectiveMode: 'day' | 'condensed' | 'expanded' = isMobile ? 'day' : viewMode;
  const dayBased = effectiveMode === 'day' || effectiveMode === 'condensed';

  const visibleDays = useMemo(() => {
    if (effectiveMode === 'day') return getDaysFromToday(condensedOffset, 1);
    if (effectiveMode === 'condensed') return getDaysFromToday(condensedOffset, CONDENSED_DAYS);
    return getWeekDays(weekOffset);
  }, [effectiveMode, weekOffset, condensedOffset]);

  const rangeStart = visibleDays[0];
  const rangeEnd = visibleDays[visibleDays.length - 1];
  const rangeFrom = formatDDMMYYYY(rangeStart);
  const rangeTo = formatDDMMYYYY(rangeEnd);
  const atToday = dayBased ? condensedOffset === 0 : weekOffset === 0;

  // Fetch the recurring plan for the currently displayed range.
  const fetchPlanItems = useCallback(async () => {
    if (!patientId) return;
    try {
      setPlanLoading(true);
      setPlanError(null);
      const items = await diaryService.getAllDiaryItemsByUserId(patientId, {
        start_date: rangeFrom,
        end_date: rangeTo,
      });
      setPlanItems(items.sort((a, b) => (a.timeOfDay || '').localeCompare(b.timeOfDay || '')));
    } catch (err) {
      console.error('Error fetching diary items:', err);
      setPlanError('No se pudo cargar el plan');
      setPlanItems([]);
    } finally {
      setPlanLoading(false);
    }
  }, [patientId, rangeFrom, rangeTo]);

  // Fetch the logged records for the currently displayed week.
  const fetchRecords = useCallback(async () => {
    if (!patientId) return;
    try {
      setRecordsLoading(true);
      const data = await diaryRecordsService.getDiaryRecords(patientId, {
        date_from: rangeFrom,
        date_to: rangeTo,
      });
      setRecords(data);
    } catch (err) {
      console.error('Error fetching diary records:', err);
      setRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  }, [patientId, rangeFrom, rangeTo]);

  useEffect(() => { fetchPlanItems(); }, [fetchPlanItems]);
  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // record lookup keyed by "diaryItem|DD-MM-YYYY"
  const recordByKey = useMemo(() => {
    const map = new Map<string, DiaryRecord>();
    for (const r of records) {
      const key = `${r.diaryItem}|${recordDatePart(r.date)}`;
      if (!map.has(key)) map.set(key, r);
    }
    return map;
  }, [records]);

  const handleEditItem = (item: DiaryItem) => {
    setEditingItem({ ...item });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedItemData: Partial<DiaryItem>) => {
    if (!editingItem || !patientId) return;
    try {
      const completeItem: DiaryItem = { ...editingItem, ...updatedItemData, id: editingItem.id };
      await diaryService.updateDiaryItem(patientId, editingItem.id, completeItem, editingItem);
      await Promise.all([fetchPlanItems(), fetchRecords()]);
      setEditModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating diary item:', error);
      alert('Error al guardar los cambios. Por favor, inténtalo de nuevo.');
    }
  };

  const handleCreatePlan = async (newItem: Partial<DiaryItem>) => {
    if (!patientId) return;
    try {
      await diaryService.createDiaryItem(patientId, newItem);
      await Promise.all([fetchPlanItems(), fetchRecords()]);
      setCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating diary item:', error);
      alert('Error al crear el plan. Por favor, inténtalo de nuevo.');
    }
  };

  const openCreate = (type: ItemType) => {
    setCreateModalItemType(type);
    setCreateDefaultDay('Monday');
    setCreateModalOpen(true);
  };

  const today = new Date();
  const monthFmt = (d: Date) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  const rangeLabel =
    effectiveMode === 'day'
      ? rangeStart.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })
      : `${monthFmt(rangeStart)} – ${rangeEnd.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  const dayStep = effectiveMode === 'condensed' ? CONDENSED_DAYS : 1;
  const goPrev = () => (dayBased ? setCondensedOffset(o => o - dayStep) : setWeekOffset(w => w - 1));
  const goNext = () => (dayBased ? setCondensedOffset(o => o + dayStep) : setWeekOffset(w => w + 1));
  const goToday = () => (dayBased ? setCondensedOffset(0) : setWeekOffset(0));

  return (
    <Section style={{ flex: 1, marginBottom: 0 }}>
      {/* Week navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={goPrev} style={navBtnStyle} aria-label="Anterior">◀</button>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#1f2937', minWidth: 180, textAlign: 'center' }}>
            {rangeLabel}
          </div>
          <button onClick={goNext} style={navBtnStyle} aria-label="Siguiente">▶</button>
          {!atToday && (
            <button onClick={goToday} style={{ ...navBtnStyle, width: 'auto', padding: '0 14px', fontWeight: 600 }}>
              Hoy
            </button>
          )}

          {/* View mode toggle (desktop only) */}
          {!isMobile && (
            <div style={{ display: 'inline-flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', marginLeft: 4 }}>
              <button
                onClick={() => setViewMode('condensed')}
                style={toggleBtnStyle(viewMode === 'condensed')}
              >
                3 días
              </button>
              <button
                onClick={() => setViewMode('expanded')}
                style={toggleBtnStyle(viewMode === 'expanded')}
              >
                Semana
              </button>
            </div>
          )}

          {recordsLoading && <span style={{ fontSize: 12, color: '#94a3b8' }}>Actualizando…</span>}
        </div>

        {/* Create buttons */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {CREATE_BUTTONS.map(btn => (
            <button
              key={btn.type}
              onClick={() => openCreate(btn.type)}
              style={{
                background: btn.bg, color: '#fff', border: 'none', borderRadius: 6,
                padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = btn.hover; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = btn.bg; }}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
              <span>{btn.icon}</span>
              <span>{btn.label}</span>
            </button>
          ))}
        </div>
      </div>

      {planLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Cargando plan…</div>
      ) : planError ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#e53e3e' }}>{planError}</div>
      ) : (
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 8 }}>
          {visibleDays.map((date) => {
            const dayIdx = (date.getDay() + 6) % 7; // 0=Monday … 6=Sunday
            const dayName = DAY_NAMES[dayIdx];
            const dateKey = formatDDMMYYYY(date);
            const isToday = isSameDay(date, today);
            const dayItems = planItems
              .filter(item =>
                (item.dayOfWeek || '').toLowerCase() === dayName.toLowerCase() &&
                isItemActiveOn(item, date)
              )
              .sort((a, b) => (a.timeOfDay || '').localeCompare(b.timeOfDay || ''));

            return (
              <div
                key={dateKey}
                style={{
                  flex: effectiveMode === 'day' ? '1 1 100%' : `1 0 ${effectiveMode === 'condensed' ? 230 : 165}px`,
                  minWidth: effectiveMode === 'day' ? 0 : effectiveMode === 'condensed' ? 230 : 165,
                  background: isToday ? '#f0fdfa' : '#ffffff',
                  border: `1px solid ${isToday ? '#5eead4' : '#e2e8f0'}`,
                  borderRadius: 10,
                  padding: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{
                  textAlign: 'center',
                  padding: '4px 0',
                  borderBottom: `2px solid ${isToday ? '#38b2ac' : '#e2e8f0'}`,
                  color: isToday ? '#0f766e' : '#475569',
                  fontWeight: 700,
                  fontSize: 13,
                }}>
                  {SHORT_DAY[dayIdx]} {date.getDate()}
                </div>
                {dayItems.length === 0 ? (
                  <div style={{ fontSize: 11, color: '#cbd5e1', textAlign: 'center', padding: '12px 0' }}>—</div>
                ) : (
                  dayItems.map(item => (
                    <PlanAgendaCard
                      key={item.id}
                      item={item}
                      record={recordByKey.get(`${item.id}|${dateKey}`)}
                      onEdit={() => handleEditItem(item)}
                      compact={effectiveMode === 'expanded'}
                    />
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}

      <DiaryItemCreateModal
        open={editModalOpen}
        onSave={handleSaveEdit}
        onCancel={() => { setEditModalOpen(false); setEditingItem(null); }}
        editingItem={editingItem}
        mode="edit"
        patientUserId={patientUserId}
        patientId={patientId}
      />
      <DiaryItemCreateModal
        open={createModalOpen}
        onSave={handleCreatePlan}
        onCancel={() => setCreateModalOpen(false)}
        defaultDayOfWeek={createDefaultDay}
        defaultItemType={createModalItemType}
        patientUserId={patientUserId}
        patientId={patientId}
      />
    </Section>
  );
};

const toggleBtnStyle = (active: boolean): React.CSSProperties => ({
  border: 'none',
  background: active ? '#38b2ac' : '#fff',
  color: active ? '#fff' : '#475569',
  fontSize: 13,
  fontWeight: 600,
  padding: '7px 14px',
  cursor: 'pointer',
});

const navBtnStyle: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#334155',
  fontSize: 14,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export default PlanPanel;
